import { inventory } from '@ctrl/db';
import { eq, useLiveQuery } from '@tanstack/react-db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { SelectField, TextField } from '@/components/form-fields';
import { useDetailTitle } from '@/components/page-header';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { adminApi } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';
import {
  type InventoryLevelRecord,
  inventoryLevelsCollection,
  locationsCollection,
  variantsCollection,
} from '@/lib/collections';
import { supabase } from '@/lib/supabase';

/**
 * One shelf: a variant at a location.
 *
 * The numbers are not editable here. On-hand moves through `inventory_move`,
 * which writes the ledger and the running total together -- editing the
 * column directly would leave stock that nobody can account for. The
 * threshold is a plain setting and is written straight from the browser.
 */
export const Route = createFileRoute('/inventory/$itemId')({
  /**
   * The stores this screen reads are synced on demand; without a
   * preload the first render sees empty tables and shows raw ids
   * where it means to show names.
   */
  loader: async () => {
    await Promise.all([
      inventoryLevelsCollection.preload(),
      variantsCollection.preload(),
      locationsCollection.preload(),
    ]).catch(() => undefined);
  },
  component: InventoryDetailPage,
});

/**
 * The reasons the ledger accepts, from the repository that defines them.
 *
 * Not a list written here: `inventory_move` validates the reason against
 * this exact set, and each one also fixes a direction -- "received" cannot
 * take stock away — which the API checks before the move.
 */
const REASONS = inventory.ADJUST_REASONS.map((reason) => ({
  value: reason.value,
  label: reason.label,
}));

function InventoryDetailPage() {
  const { itemId } = useParams({ from: '/inventory/$itemId' });
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();

  const canManage = can('inventory.manage');
  const levelId = Number(itemId);

  const found = useLiveQuery((query) =>
    query.from({ level: inventoryLevelsCollection }).where(({ level }) => eq(level.id, levelId)),
  );

  const level = (found.data?.[0] ?? null) as InventoryLevelRecord | null;

  const variants = useLiveQuery((query) => query.from({ variant: variantsCollection }));
  const locations = useLiveQuery((query) => query.from({ location: locationsCollection }));

  const variant = (variants.data ?? []).find((row) => row.id === level?.variant_id);
  const location = (locations.data ?? []).find((row) => row.id === level?.location_id);

  useDetailTitle(variant?.name ?? 'Stock');

  const movements = useQuery({
    queryKey: ['stock-movements', accountId, itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stock_movements')
        .select('id, type, quantity, balance_after, reason, created_at, actor_name')
        .eq('account_id', accountId)
        .eq('variant_id', level?.variant_id as string)
        .eq('location_id', level?.location_id as string)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      return data ?? [];
    },
    enabled: Boolean(accountId && level),
  });

  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState<string>(REASONS[0]?.value ?? 'manual');
  const [note, setNote] = useState('');

  const adjust = useMutation({
    mutationFn: () => {
      const quantityDelta = Number(delta);

      if (!Number.isFinite(quantityDelta) || quantityDelta === 0) {
        throw new Error('Enter how much to add or take away.');
      }

      return adminApi.inventory.adjust(accountId, levelId, {
        quantityDelta,
        reason,
        note: note.trim(),
      });
    },
    onSuccess: (result) => {
      setDelta('');
      setNote('');
      toast.success(`On hand is now ${result.onHand}.`);
      queryClient.invalidateQueries({ queryKey: ['stock-movements', accountId, itemId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (found.isLoading) return <Skeleton className="h-64 w-full" />;

  if (!level) {
    return (
      <EmptyState>
        No such stock record.{' '}
        <Link to="/inventory" className="underline">
          Back to inventory
        </Link>
      </EmptyState>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{variant?.name ?? 'Variant'}</h1>
        <p className="text-muted-foreground text-sm">{location?.name ?? 'Location'}</p>
      </div>

      {!canManage && <ReadOnlyNotice permission="inventory.manage" />}

      <div className="grid gap-4 sm:grid-cols-3">
        <Count label="On hand" value={level.on_hand} />
        <Count label="Reserved" value={level.reserved} />
        <Count label="Available" value={level.available} />
      </div>

      <SectionCard
        title="Adjust"
        description="Goes through `inventory_move`, which writes the ledger and the total together."
      >
        <fieldset disabled={!canManage} className="flex flex-wrap items-end gap-3">
          <TextField
            id="adjust-delta"
            label="Change"
            className="w-28"
            description="Negative takes away."
            value={delta}
            onChange={setDelta}
          />
          <SelectField
            id="adjust-reason"
            label="Reason"
            className="w-40"
            value={reason}
            onChange={setReason}
            options={REASONS}
          />
          <TextField
            id="adjust-note"
            label="Note"
            className="w-56"
            value={note}
            onChange={setNote}
          />
          <Button type="button" onClick={() => adjust.mutate()} disabled={adjust.isPending}>
            {adjust.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </fieldset>
      </SectionCard>

      <SectionCard title="Low stock" description="What counts as running out on this shelf.">
        <fieldset disabled={!canManage}>
          <TextField
            id="threshold"
            label="Threshold"
            className="w-32"
            value={String(level.low_stock_threshold ?? 0)}
            onChange={(value) =>
              inventoryLevelsCollection.update(levelId, (draft) => {
                draft.low_stock_threshold = Number(value) || 0;
              })
            }
          />
        </fieldset>
      </SectionCard>

      <SectionCard title="Movements" description="Every change to this shelf, newest first.">
        {movements.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : movements.data?.length ? (
          <ul className="divide-y rounded-lg border text-sm">
            {movements.data.map((movement) => (
              <li key={movement.id} className="flex items-baseline justify-between gap-3 px-3 py-2">
                <span>
                  <span className="font-medium tabular-nums">
                    {Number(movement.quantity) > 0 ? '+' : ''}
                    {movement.quantity}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    {movement.type}
                    {movement.reason ? ` · ${movement.reason}` : ''}
                  </span>
                </span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {new Intl.DateTimeFormat('en-US', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(movement.created_at))}
                  {movement.actor_name ? ` · ${movement.actor_name}` : ''}
                  {movement.balance_after == null ? '' : ` · left ${movement.balance_after}`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>Nothing has moved yet.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}

function Count({ label, value }: { label: string; value: number | string | null }) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value ?? 0}</p>
    </div>
  );
}
